import { useCallback, useState, useEffect, useTransition } from "react";
import { useNavigateWithSearch } from "hooks/useNavigateWithSearch";
import CoverageReplacementAccordion from "components/accordions/coverage-replacement/CoverageReplacementAccordion";
import PaymentInformationAccordion from "components/accordions/payment-information/PaymentInformationAccordion";
import SecondaryAddresseeAccordion from "components/accordions/secondary-addressee/SecondaryAddresseeAccordion";
import StatementOfHealthAccordion from "components/accordions/statement-of-health/StatementOfHealthAccordion";
import YourContactInformationAccordion from "components/accordions/your-contact-information/YourContactInformationAccordion";
import YourPolicyBeneficiaryAccordion from "components/accordions/your-policy-beneficiary/YourPolicyBeneficiaryAccordion";
import CardNavigationButtons from "components/card-navigation-button/CardNavigationButtons";
import Agreement from "components/card/agreement/Agreement";
import MLTAAgreement from "components/card/agreement/MLTAAgreement";
import ElectronicSignatureConsent from "components/electronic-signature-consent/ElectronicSignatureConsent";
import { useSubmission } from "hooks/useSubmission";
import { useQuote } from "hooks/useQuote";
import { useContactInformation } from "hooks/useContactInformation";
import { useProgressBar } from "context/ProgressBarContext";
import { ROUTES } from "routes/constants";
import { useProductContext } from "context/ProductContext";

const ReviewInformationPage = () => {
    // ========== Hooks ==========
    const navigate = useNavigateWithSearch();
    const { submitApplication } = useSubmission();
    const { quoteData } = useQuote();
    const { contactData } = useContactInformation();
    const { updateStepCompletion } = useProgressBar();
    const { isMLTA, isGIWL, isDTO } = useProductContext();

    // ========== State ==========
    const [electronicSignatureConsent, setElectronicSignatureConsent] = useState(false);
    const [consentError, setConsentError] = useState<string>("");
    const [isSubmitting, startSubmitTransition] = useTransition();

    // ========== Computed Values ==========
    // Blocks rendering/resubmission when data is missing; the submitted-application
    // case (back-navigation after submit) is handled by SubmittedApplicationGuardLayout
    const isMissingRequiredData = !quoteData || !contactData;

    // ========== Effects ==========
    // Validate that required data exists
    useEffect(() => {
        if (isMissingRequiredData) {
            console.error("ReviewInformationPage: Missing required data", { quoteData, contactData });
            navigate(ROUTES.SOMETHING_WENT_WRONG, { replace: true });
        }
    }, [isMissingRequiredData, quoteData, contactData, navigate]);

    // Update mini progress bar for Step 2 (Review)
    useEffect(() => {
        const percent = electronicSignatureConsent ? 100 : 0;
        updateStepCompletion(2, percent);
    }, [electronicSignatureConsent, updateStepCompletion]);

    // Clear error when consent is given
    useEffect(() => {
        if (electronicSignatureConsent && consentError) {
            setConsentError("");
        }
    }, [electronicSignatureConsent, consentError]);

    // ========== Event Handlers ==========
    const handleBack = useCallback(() => {
        navigate(ROUTES.APPLICATION.PAYMENT);
    }, [navigate]);

    const handleSubmit = useCallback(() => {
        if (!electronicSignatureConsent) {
            setConsentError("You must agree before continuing");
            return;
        }

        setConsentError("");
        startSubmitTransition(async () => {
            const result = await submitApplication();
            navigate(result.route);
        });
    }, [electronicSignatureConsent, startSubmitTransition, submitApplication, navigate]);

    // ========== Render ==========
    if (isMissingRequiredData) return null;

    return (
        <>
            <title>Review Info | AAA Life</title>
            <main className="flex flex-col gap-6 items-center" role="main" aria-label="Review and Submit Application">
                <section aria-labelledby="review-heading">
                    <h1 id="review-heading" className="text-base-content text-2xl font-semibold text-center">
                        Review Your Information
                    </h1>
                </section>

                <section aria-labelledby="review-heading" className="w-full flex justify-center">
                    <fieldset className="flex flex-col gap-2 w-full max-w-3xl items-center">
                        <legend className="sr-only">Review Information Sections</legend>
                        <YourContactInformationAccordion />
                        {isDTO && <StatementOfHealthAccordion />}
                        {isDTO && <CoverageReplacementAccordion />}
                        <YourPolicyBeneficiaryAccordion />
                        <SecondaryAddresseeAccordion />
                        {isGIWL && <CoverageReplacementAccordion />}
                        <PaymentInformationAccordion />
                    </fieldset>
                </section>

                <section aria-labelledby="agreement-heading" className="w-full flex flex-col gap-6 items-center">
                    <h2 id="agreement-heading" className="text-base-content text-2xl font-semibold">
                        Agreement
                    </h2>
                    {isMLTA || isGIWL ? (
                        <>
                            <MLTAAgreement
                                checked={electronicSignatureConsent}
                                onChange={setElectronicSignatureConsent}
                                hasError={!!consentError}
                            />
                        </>
                    ) : (
                        <>
                            <Agreement />
                            <div className="flex flex-col gap-1">
                                <ElectronicSignatureConsent
                                    checked={electronicSignatureConsent}
                                    onChange={setElectronicSignatureConsent}
                                    hasError={!!consentError}
                                />
                            </div>
                        </>
                    )}
                </section>
            </main>
            <CardNavigationButtons onBack={handleBack} onNext={handleSubmit} isLoading={isSubmitting} nextButtonText="Submit" />
        </>
    );
};

export { ReviewInformationPage };
export default ReviewInformationPage;
